import { DEFAULT_COUNTRY_CODE } from '@/utils/constants'

const ACCOUNT_INFO = {
  accountType: '',
  accountId: '',
  userKey: null,
  email: '',
  mobilePhone: null
}
const CLASSROM_INFO = {
  classroomName: '',
  studentName: '',
  studentNo: ''
}
const PROFILE_INFO = {
  ppsn: '',
  profileName: '',
  profileCode: '',
  profileImageUrl: '',
  badges: [],
  verifiedBadge: '',
  bans: [],
  useAutoUpdateProfile: false,
  classroomInfo: CLASSROM_INFO
}

export const useAuthStore = defineStore('auth', () => {
  const nuxtApp = useNuxtApp()
  const route = useRoute()
  const router = useRouter()
  const { accessToken, refreshToken } = useAuthToken()
  //const accessToken = useCookie('_a_t', { path: '/', })
  const localePath = useLocalePath()

  /* state */
  // auth/v1/token/login 호출 시 terms가 항상 빈 배열로 넘어오는 걸로 보여 서버 문의 중
  // TODO: 실제 타입 확인 후 적용 예정
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const terms = ref<Array<any>>([])
  const account = ref<AccountInfo>({ ...ACCOUNT_INFO })
  const profile = ref<ProfileInfo>({ ...PROFILE_INFO })
  const countryCodeOfGeoIP = ref<CountryCodeOfGeoIP>(DEFAULT_COUNTRY_CODE)

  /* getters */

  // 프로필 여부
  const hasProfile = computed(() => {
    // 학생은 약관 동의 여부로 프로필 존재 여부를 판단
    // 일반, 선생님은 프로필 코드로 판단
    return !!profile.value.profileCode /* (
      (account.value.accountType === STUDENT && terms.value.length === 0) ||
      (account.value.accountType !== STUDENT && !!profile.value.profileCode)
    ) */
  })
  const profileCode = computed(() => profile.value.profileCode)
  const profileName = computed(() => profile.value.profileName)
  const accountType = computed(() => account.value.accountType)
  const classroom = computed(() => profile.value.classroomInfo)
  const isRequiredInit = computed(() => {
    return !!accessToken.value && hasProfile.value === false
  })
  const isKR = computed(() => countryCodeOfGeoIP.value === 'KR')

  /* actions */
  const setProfile = (newProfile: ProfileInfo) => {
    profile.value = { ...profile.value, ...newProfile }
  }
  const $reset = () => {
    terms.value = []
    account.value = { ...ACCOUNT_INFO }
    profile.value = { ...PROFILE_INFO }
  }

  const setAccount = (newAccount: AccountInfo) => {
    account.value = { ...account.value, ...newAccount }
  }

  // TODO: 실제 타입 확인 후 적용 예정
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setTerms = (newTerms: Array<any>) => {
    console.log('setTerms', newTerms)
    terms.value = newTerms
  }

  const login = async ({
    userId,
    password
  }: {
    userId: string
    password: string
  }): Promise<number> => {
    try {
      const resp = await nuxtApp.$api.auth.login({
        userId,
        password
      })
      if (resp) {
        const { data, code } = resp
        if (code === 0 && data) {
          const {
            accessToken: _at,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            accessTokenExpiresIn,
            refreshToken: _rt,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            refreshTokenExpiresIn,
            terms,
            profileInfo: { accountInfo, ...restProfile }
          } = data
          accessToken.value = _at
          refreshToken.value = _rt
          /*  updateToken({
            type: ACCESS_TOKEN_KEY,
            newToken: accessToken,
            maxAge: accessTokenExpiresIn
          })
          updateToken({
            type: REFRESH_TOKEN_KEY,
            newToken: refreshToken,
            maxAge: refreshTokenExpiresIn
          }) */
          setTerms(terms)
          setAccount(accountInfo)
          setProfile(restProfile)

          // 프로필 이름이 없는 경우
          // auccontType이 GENERAL|TEACHER
        }

        // 학생계정
        return code
      }

      throw new Error('login failed')
    } catch (e) {
      console.log(e)
      return -1
    }
  }
  const logout = async (redirectPath?: string) => {
    try {
      // 성공 여부는 의미 없음
      if (accessToken.value) {
        await nuxtApp.$api.auth.logout()
      }
    } catch (e) {
      console.log(e)
    } finally {
      $reset()
      accessToken.value = null
      refreshToken.value = null
      if (redirectPath) {
        router.push(redirectPath)
      } else {
        const routeName = route.name?.toString() ?? ''
        if (/^(settings|init|nickname)/.test(routeName)) {
          router.push(localePath('/'))
        }
      }
    }

    return true
  }

  const getMyProfile = async () => {
    try {
      console.log('getMyProfile')
      const resp = await nuxtApp.$api.profile.getMyProfile()

      if (resp) {
        const { code, data } = resp
        switch (code) {
          case 0:
            {
              if (data) {
                // 프로필 정보 저장
                console.log('valid profile', resp)
                const { terms, profileInfo } = data
                if (profileInfo) {
                  const { accountInfo, ...restProfile } = profileInfo
                  setAccount(accountInfo)
                  setProfile(restProfile)
                }

                setTerms(terms ?? [])
              }
            }

            break
          case PROFILE_ERROR_CODE.NO_PROFILE:
            setProfile({
              ppsn: '',
              profileName: '',
              profileCode: '',
              profileImageUrl: ''
            })
            break

          default:
            // error
            break
        }
        return code
      }
      return -1
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.log('getMyProfile err', e)
      if (import.meta.client) {
        if (e.name === 'FetchError' && e.message?.includes('aborted')) {
          return
        }
      }

      return e?.code ?? -1
    }
  }

  const setCountryCodeOfGeoIP = (newCountryCodeOfGeoIP: CountryCodeOfGeoIP) => {
    countryCodeOfGeoIP.value = newCountryCodeOfGeoIP
  }

  return {
    hasProfile,
    terms,
    account,
    profile,
    profileCode,
    profileName,
    accountType,
    isRequiredInit,
    classroom,
    countryCodeOfGeoIP,
    isKR,
    setAccount,
    setTerms,
    setProfile,
    setCountryCodeOfGeoIP,
    login,
    logout,
    getMyProfile
    /*     checkHasProfileAfterLogin,
    checkAfterSignUp */
  }
})
